import { saveServiceWorkOrder } from '../service-work-orders/saveServiceWorkOrder';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card } from 'react-bootstrap';
import { X, ArrowDownCircle, ArrowUpCircle } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import { v4 as uuid } from 'uuid';
import FormGenerator from '../../form-generator/FormGenerator';
import { ABORT_ERROR, makeApiRequests } from '../../helpers/api';
import { ENDPOINTS, contractMilestoneDates, typeOfTransactionOptions } from '../../helpers/constants';
import {
  calculateCommissionsSummary,
  calculateSalesSummary,
  documentFieldsHasInventoryCommissionPaths,
  getDocHubFormJSON,
  validateAndCleanupDocHubForm
} from '../../helpers/docHubFormHelpers';
import { convertKeyValueToFormObject, getObjectForPrefill } from '../../helpers/formHelpers';
import AlertModal from '../AlertModal';
import SlidingSideBar from '../SlidingSideBar/SlidingSideBar';
import FilePopup from '../common/FilePopup';
import ServiceReportModal from '../common/ServiceReportModal';
import ContractedUnitOverview from '../inventory/ContractedUnitOverview';
import EditInventory from '../inventory/EditInventory';
import InventoryCommissionFields from '../inventory/InventoryCommissionFields';
import InventoryPnlFields from '../inventory/InventoryPnlFields';
import InventoryPicker from '../inventory/InventoryPicker';
import PotentialUnitsOverview from '../inventory/PotentialUnitsOverview';
import { getDefaultMockCommissionFields } from '../inventory/inventoryCommissionHelpers';
import { cloneDeep } from 'lodash';
import './PrintGroupSelectionBox.scss';

const InventoryEditSideBar = ({
  editingInventory,
  appChoices,
  onHide,
  onInventoryDelete,
  onInventoryEdit,
  refetchChoices
}) => {
  return (
    <SlidingSideBar fullScreen visible={editingInventory} onClose={onHide} title={'Inventory Details'} zIndex={10001}>
      {editingInventory && (
        <EditInventory
          id={editingInventory['_id']}
          inventoryFromSearch={editingInventory}
          appChoices={appChoices}
          fromSearch
          onInventoryDelete={onInventoryDelete}
          onInventoryEdit={onInventoryEdit}
          refetchChoices={refetchChoices}
        />
      )}
    </SlidingSideBar>
  );
};

const DocumentPrinter = ({
  appChoices,
  documentFields,
  docSource,
  selectedGroup,
  onServiceOrderReportUpdate,
  documentId,
  documentName,
  isServiceRequest,
  isMockPrint,
  onUpdateDocHub,
  refetchChoices,
  initialInventoryUnit,
  alwaysShowInventoryCommissionFields = false
}) => {
  const [formPreFill, setFormPreFill] = useState(null);
  const [formJson, setFormJson] = useState(null);
  const [inventoryUnitMeta, setInventoryUnitMeta] = useState(null);
  const [showServiceReportModal, setShowServiceReportModal] = useState(false);
  const [serviceOrderRows, setServiceOrderRows] = useState(null);
  const [serviceRowSubmitting, setServiceRowSubmitting] = useState(false);
  const [saveMode, setSaveMode] = useState(true);
  const [printingDocument, setPrintingDocument] = useState(false);
  const [savingData, setSavingData] = useState(false);

  const [viewingFile, setViewingFile] = useState(null);
  const [createdDocument, setCreatedDocument] = useState(null);
  const [customMockCommissionFields, setCustomMockCommissionFields] = useState(null);
  const [commissionOptionsCollapsed, setCommissionOptionsCollapsed] = useState(false);
  const [draftCommissionPayload, setDraftCommissionPayload] = useState(null);

  const abortControllerRef = useRef(null);
  const createdDocumentAlertRef = useRef(null);
  const commissionFieldsRef = useRef(null);
  const pnlFieldsRef = useRef(null);
  const mockCommissionManualFieldsRef = useRef(null);

  const showInventoryCommissionFields = useMemo(
    () =>
      alwaysShowInventoryCommissionFields ||
      documentFieldsHasInventoryCommissionPaths(documentFields),
    [alwaysShowInventoryCommissionFields, documentFields]
  );

  const resolvePickedInventory = (pickedInventory, inventoryUnits = []) => {
    if (!pickedInventory?._id) return pickedInventory;

    if (docSource === 'Contact' && inventoryUnits.length) {
      return inventoryUnits.find(unit => unit._id === pickedInventory._id) || pickedInventory;
    }

    return pickedInventory;
  };

  const selectedInventoryForCommission = useMemo(() => {
    if (!inventoryUnitMeta?.pickedInventory) return null;
    return resolvePickedInventory(inventoryUnitMeta.pickedInventory, inventoryUnitMeta.inventoryUnits);
  }, [docSource, inventoryUnitMeta?.pickedInventory, inventoryUnitMeta?.inventoryUnits]);

  const getInventoryUnitForCommissionSummary = () => {
    if (selectedInventoryForCommission) {
      return draftCommissionPayload
        ? { ...selectedInventoryForCommission, ...draftCommissionPayload }
        : selectedInventoryForCommission;
    }

    if (isMockPrint && customMockCommissionFields) {
      return customMockCommissionFields;
    }

    return null;
  };

  useEffect(() => {
    if (!documentFields) {
      setFormPreFill(null);
      setFormJson(null);
    }

    const tempDocumentFields = { ...documentFields };

    //array to object
    const initialFormValues = (tempDocumentFields['withData'] || []).reduce(
      (prevValue, newObject) => ({ ...prevValue, [newObject.name]: newObject.value }),
      {}
    );

    tempDocumentFields['withData'] && setFormPrefillValues(initialFormValues);
    setForm(tempDocumentFields, initialFormValues);
  }, [documentFields]);

  useEffect(() => {
    if (!createdDocument || !createdDocumentAlertRef.current) return;
    createdDocumentAlertRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [createdDocument]);

  useEffect(() => {
    try {
      calculateCommissionsSummary(getInventoryUnitForCommissionSummary());
    } catch (e) {
      console.log('calculateCommissionsSummary', e);
    }
  }, [inventoryUnitMeta, formJson, customMockCommissionFields, selectedInventoryForCommission, draftCommissionPayload]);

  useEffect(() => {
    setDraftCommissionPayload(null);
  }, [selectedInventoryForCommission?._id]);

  useEffect(() => {
    if (
      isMockPrint &&
      showInventoryCommissionFields &&
      appChoices?.length &&
      !inventoryUnitMeta?.inventoryUnits?.length &&
      !inventoryUnitMeta?.pickedInventory
    ) {
      setCustomMockCommissionFields(prev => prev || getDefaultMockCommissionFields(appChoices));
    }
  }, [
    isMockPrint,
    showInventoryCommissionFields,
    appChoices,
    inventoryUnitMeta?.inventoryUnits?.length,
    inventoryUnitMeta?.pickedInventory
  ]);

  useEffect(() => {
    if (inventoryUnitMeta?.inventoryUnits?.length || inventoryUnitMeta?.pickedInventory) {
      setCustomMockCommissionFields(null);
    }
  }, [inventoryUnitMeta?.inventoryUnits?.length, inventoryUnitMeta?.pickedInventory]);

  const setFormPrefillValues = initialFormValues => {
    const data = cloneDeep(initialFormValues);
    //convert contract important dates to linear fields
    contractMilestoneDates.forEach(({ key, start, end, emailField, options }) => {
      let milestoneObject = data[key];
      if (milestoneObject) {
        data[start] = milestoneObject.start;
        data[end] = milestoneObject.end;
        data[emailField] = milestoneObject.notificationEmails
          ? milestoneObject.notificationEmails.map(e => (e === data['buyerEmail'] ? 'buyer-mhc' : e))
          : [];
        data[options] = milestoneObject.options;
      }
    });

    //manage type of transaction
    if (
      data['typeOfTransaction'] &&
      ['Real |', 'Personal |'].some(v => data['typeOfTransaction'].includes(v)) &&
      !typeOfTransactionOptions.includes(data['typeOfTransaction'])
    ) {
      const typeOtherValue = data['typeOfTransaction'].split(' | ');
      data['typeOfTransaction'] = `${typeOtherValue[0]} | Other`;
      data['otherType'] = typeOtherValue[1];
    }

    convertKeyValueToFormObject(data, appChoices);
    setFormPreFill(getObjectForPrefill(data));
  };

  const setForm = (documentFields = [], initialFormValues = {}) => {
    //TODO check why timeout?
    setTimeout(() => {
      createNewForm(
        documentFields,
        ['Commission Sheet', 'Sales Agreement'].includes(documentName)
          ? documentName
          : documentFields.includedDocument?.find(n => ['Commission Sheet', 'Sales Agreement'].includes(n)),
        initialFormValues,
        documentName
      );
    }, 200);
  };

  /**
   * Takes form fields from response
   * and renders a form to fill up variables required for this document
   * summaryFieldType [User for showing summary section below form]
   */
  const createNewForm = (initJson, summaryFieldType, initialFormValues, documentName) => {
    //handle inventory unit (need to show inventory picker if document has inventory unit)
    handleInventoryUnit(initJson);
    setFormJson(getDocHubFormJSON(initJson, summaryFieldType, appChoices, docSource, isMockPrint, initialFormValues, documentName));
  };

  const handleInventoryUnit = ({ withData = [], withoutData = [] }) => {
    const inventoryUnitWithOutDataIndex = withoutData.findIndex(f => f.name === 'inventoryUnit');
    if (inventoryUnitWithOutDataIndex !== -1) {
      if (isMockPrint && initialInventoryUnit) {
        setInventoryUnitMeta({
          inventoryUnits: [initialInventoryUnit],
          pickedInventory: initialInventoryUnit
        });
      } else {
        setInventoryUnitMeta(['Contract', 'Inventory'].includes(docSource) ? {} : { inventoryUnits: [] });
      }
      withoutData.splice(inventoryUnitWithOutDataIndex, 1);
    }

    const inventoryUnitWithDataIndex = withData.findIndex(f => f.name === 'inventoryUnit');
    if (inventoryUnitWithDataIndex !== -1) {
      setInventoryUnitMeta(
        ['Contract', 'Inventory'].includes(docSource)
          ? { pickedInventory: withData[inventoryUnitWithDataIndex]['value'] }
          : {
            inventoryUnits: withData[inventoryUnitWithDataIndex]['value'],
            pickedInventory: withData[inventoryUnitWithDataIndex]['value']?.[0]
          }
      );
      withData.splice(inventoryUnitWithDataIndex, 1);
    }
  };

  const onUnitSelect = unit => {
    setInventoryUnitMeta({ ...inventoryUnitMeta, selectedUnit: unit });
  };

  const onServiceOrderReportClick = () => {
    setShowServiceReportModal(true);
  };

  const handleServiceReportClose = () => {
    setServiceOrderRows(null);
    setShowServiceReportModal(false);
  };

  const onServiceOrderRowsChange = rows => {
    setServiceOrderRows(rows);
  };

  const submitServiceReport = async () => {
    setServiceRowSubmitting(true);
    const submitRows = serviceOrderRows.map(r => {
      const obj = {
        ...r,
        Description: r['Description'] === 'Other' ? r['Description Other'] : r['Description'],
        Cause: r['Cause'] === 'Other' ? r['Cause Other'] : r['Cause'],
        'Repair Method': r['Repair Method'] === 'Other' ? r['Repair Method Other'] : r['Repair Method']
      };

      ['Repair Method Other', 'Cause Other', 'Description Other'].forEach(f => delete obj[f]);

      return obj;
    });

    const { response, error } = await saveServiceWorkOrder(selectedGroup, submitRows);

    if (error) {
      setServiceRowSubmitting(false);
      toast.error(error);
      return;
    }

    toast.success('Service Order Report submitted successfully!');
    setServiceRowSubmitting(false);
    onServiceOrderReportUpdate({ ...selectedGroup, serviceOrderReport: response.serviceOrderReport });
    handleServiceReportClose();
  };

  const onSaveClick = () => {
    setSaveMode(true);
    setTimeout(() => document.getElementById('document').click(), 200);
  };

  const cancelPrinting = () => {
    // If an old API call is in progress, abort it
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (printingDocument.printSessionId)
      makeApiRequests({ endpoint: ENDPOINTS.DOCHUB_PRINT_DOCUMENT_CANCEL(printingDocument.printSessionId) });

    setPrintingDocument(false);
    setSavingData(false);
  };

  const onPrintClick = () => {
    if (printingDocument) {
      cancelPrinting();
      return;
    }

    setSaveMode(false);
    setTimeout(() => document.getElementById('document').click(), 200);
  };

  const onLinkUnitClick = replacingInventoryUnit => {
    setInventoryUnitMeta({ ...inventoryUnitMeta, showInventoryPicker: true, replacingInventoryUnit });
  };

  const removeInventoryUnitFromSelections = removingInventoryUnit => {
    const { inventoryUnits: previousUnits = [], pickedInventory } = inventoryUnitMeta;

    let newInventoryUnits;
    if (docSource === 'Contact') {
      newInventoryUnits = [...previousUnits];
      const removingIndex = newInventoryUnits.findIndex(u => u._id === removingInventoryUnit._id);
      newInventoryUnits.splice(removingIndex, 1);
    }

    setInventoryUnitMeta({
      ...inventoryUnitMeta,
      inventoryUnits: newInventoryUnits,
      pickedInventory:
        docSource === 'Contract' ||
          (docSource === 'Contact' && pickedInventory && pickedInventory['_id'] === removingInventoryUnit['_id'])
          ? undefined
          : pickedInventory,
      deletingUnit: false,
      toBeDeletedUnit: undefined
    });
  };

  const onRemoveUnitClick = removingInventoryUnit => {
    if (isMockPrint) {
      removeInventoryUnitFromSelections(removingInventoryUnit);
      return;
    }

    setInventoryUnitMeta({ ...inventoryUnitMeta, deletingUnit: false, toBeDeletedUnit: removingInventoryUnit || {} }); //{} for contact because there is only one unit
  };

  const updateContactOrContractInventory = async inventoryUnit => {
    setInventoryUnitMeta({ ...inventoryUnitMeta, assigningInventory: true });

    const { response, error } = await makeApiRequests({
      endpoint:
        docSource === 'Contact'
          ? ENDPOINTS.CONTACTS_WITH_ID(selectedGroup._id)
          : ENDPOINTS.CONTRACTS_WITH_ID(selectedGroup._id),
      requestBody: { inventoryUnit },
      method: 'PUT'
    });

    setInventoryUnitMeta({ ...inventoryUnitMeta, assigningInventory: false });

    if (error) {
      toast(error, {
        type: 'error'
      });
      return;
    }

    toast(`Inventory unit updated successfully!`, {
      type: 'success'
    });

    return true;
  };

  const onInventoryPicked = async inventoryUnits => {
    const inventoryUnit = inventoryUnits[0];

    const { inventoryUnits: previousUnits = [], pickedInventory, replacingInventoryUnit } = inventoryUnitMeta;

    let newInventoryUnits;
    if (docSource === 'Contact') {
      newInventoryUnits = [...previousUnits];
      if (replacingInventoryUnit) {
        const replacingIndex = newInventoryUnits.findIndex(u => u._id === replacingInventoryUnit._id);
        newInventoryUnits[replacingIndex] = inventoryUnit;
      } else {
        newInventoryUnits.push(inventoryUnit);
      }
    }

    //is mock print, no need to send a request
    if (!isMockPrint) {
      const success = await updateContactOrContractInventory(
        docSource === 'Contact' ? newInventoryUnits.map(u => u._id) : inventoryUnit._id
      );
      if (!success) return;
    }

    setInventoryUnitMeta({
      ...inventoryUnitMeta,
      inventoryUnits: newInventoryUnits,
      pickedInventory:
        docSource === 'Contract'
          ? inventoryUnit
          : (() => {
            const selectedId =
              replacingInventoryUnit && pickedInventory?._id === replacingInventoryUnit._id
                ? inventoryUnit._id
                : pickedInventory && newInventoryUnits.some(unit => unit._id === pickedInventory._id)
                  ? pickedInventory._id
                  : newInventoryUnits[0]?._id;

            return newInventoryUnits.find(unit => unit._id === selectedId);
          })(),
      showInventoryPicker: false
    });
  };

  const onInventoryPickerClose = () => {
    setInventoryUnitMeta({ ...inventoryUnitMeta, showInventoryPicker: false });
  };

  const removeInventoryUnit = async () => {
    setInventoryUnitMeta({ ...inventoryUnitMeta, deletingUnit: true });

    let inventoryUnitId;
    if (docSource === 'Contact') {
      let newUnitIds = inventoryUnitMeta['inventoryUnits'].map(u => u['_id']);
      const removingIndex = newUnitIds.indexOf(inventoryUnitMeta['toBeDeletedUnit']['_id']);
      newUnitIds.splice(removingIndex, 1);
      inventoryUnitId = newUnitIds;
    } else {
      inventoryUnitId = null;
    }

    const { response, error } = await makeApiRequests({
      endpoint:
        docSource === 'Contact'
          ? ENDPOINTS.CONTACTS_WITH_ID(selectedGroup._id)
          : ENDPOINTS.CONTRACTS_WITH_ID(selectedGroup._id),
      requestBody: { inventoryUnit: inventoryUnitId },
      method: 'PUT'
    });

    setInventoryUnitMeta({ ...inventoryUnitMeta, deletingUnit: false });

    if (error) {
      toast(error, {
        type: 'error'
      });
      return;
    }

    toast(`Inventory unit removed successfully!`, {
      type: 'success'
    });

    setInventoryUnitMeta({
      ...inventoryUnitMeta,
      inventoryUnits: docSource === 'Contact' ? response['inventoryUnit'] : undefined,
      pickedInventory:
        docSource === 'Contract' ||
          (docSource === 'Contact' &&
            inventoryUnitMeta['pickedInventory'] &&
            inventoryUnitMeta['pickedInventory']['_id'] === inventoryUnitMeta['toBeDeletedUnit']['_id'])
          ? undefined
          : resolvePickedInventory(inventoryUnitMeta['pickedInventory'], response['inventoryUnit']),
      deletingUnit: false,
      toBeDeletedUnit: undefined
    });
  };

  const onRefreshUnitClick = async () => {
    setInventoryUnitMeta({ ...inventoryUnitMeta, refreshingInventory: true });

    const { response, error } = await makeApiRequests({
      endpoint: docSource === 'Contract' ? ENDPOINTS.CONTRACTS_SEARCH : ENDPOINTS.CONTACTS_SEARCH,
      requestBody: { filter: { _id: selectedGroup['_id'] } }
    });

    setInventoryUnitMeta({ ...inventoryUnitMeta, refreshingInventory: false });
    if (error) {
      return toast.error(error);
    }

    const details = response[0];

    const refreshedInventoryUnits = docSource === 'Contact' ? details['inventoryUnit'] : undefined;
    const selectedInventoryId = inventoryUnitMeta['pickedInventory']?._id;

    setInventoryUnitMeta({
      ...inventoryUnitMeta,
      inventoryUnits: refreshedInventoryUnits,
      pickedInventory:
        docSource === 'Contract'
          ? details['inventoryUnit']
          : selectedInventoryId && refreshedInventoryUnits?.some(unit => unit._id === selectedInventoryId)
            ? refreshedInventoryUnits.find(unit => unit._id === selectedInventoryId)
            : undefined
    });
  };

  const updateInventoryInMeta = (updatedInventory, extraMeta = {}) => {
    setInventoryUnitMeta(prev => {
      const next = { ...prev, ...extraMeta };

      if (prev.pickedInventory?._id === updatedInventory._id) {
        next.pickedInventory = updatedInventory;
      }

      if (prev.inventoryUnits) {
        next.inventoryUnits = prev.inventoryUnits.map(unit =>
          unit._id === updatedInventory._id ? updatedInventory : unit
        );
      }

      return next;
    });
  };

  const onInventoryEdit = updatedInventory => {
    updateInventoryInMeta(updatedInventory, { editingInventory: null });
  };

  const onInventoryCommissionUpdate = updatedInventory => {
    updateInventoryInMeta(updatedInventory);
    setDraftCommissionPayload({
      commissionSheetOptions: updatedInventory.commissionSheetOptions,
      commissionSheetLessOptions: updatedInventory.commissionSheetLessOptions,
      pnlOptions: updatedInventory.pnlOptions
    });
  };

  const onDraftInventoryFieldsChange = payload => {
    setDraftCommissionPayload(prev => ({ ...(prev || {}), ...payload }));
  };

  const onInventoryDelete = deletedInventoryId => {
    const { pickedInventory, inventoryUnits } = inventoryUnitMeta;
    //update pickedInventory and inventoryUnits

    if (pickedInventory && pickedInventory['_id'] === deletedInventoryId) {
      inventoryUnitMeta.pickedInventory = undefined;
    }

    if (inventoryUnits) {
      const existingIndex = inventoryUnits.findIndex(u => u['_id'] === deletedInventoryId);
      if (existingIndex !== -1) {
        inventoryUnitMeta['inventoryUnits'].splice(existingIndex, 1);
      }
    }

    setInventoryUnitMeta({ ...inventoryUnitMeta, editingInventory: null });
  };

  const onPrintSubmit = async form => {
    if (!isMockPrint && !saveMode && inventoryUnitMeta) {
      if (docSource === 'Contract' && !inventoryUnitMeta['pickedInventory']) {
        return toast.error('Please link a inventory unit!');
      }

      if (docSource === 'Contact') {
        if (inventoryUnitMeta['inventoryUnits'].length === 0) {
          return toast.error('Please link potential units and pick one of them to be printed for this document!');
        }

        if (!inventoryUnitMeta['pickedInventory']) {
          return toast.error('Please pick one of the potential units to be printed for this document!');
        }
      }
    }

    const valid = validateAndCleanupDocHubForm(form);
    if (!valid) return;

    if (showInventoryCommissionFields && selectedInventoryForCommission) {
      if (commissionFieldsRef.current) {
        const commissionSaved = await commissionFieldsRef.current.save();
        if (!commissionSaved) return;
      }

      if (pnlFieldsRef.current) {
        const pnlSaved = await pnlFieldsRef.current.save();
        if (!pnlSaved) return;
      }
    } else if (
      isMockPrint &&
      showInventoryCommissionFields &&
      !selectedInventoryForCommission &&
      customMockCommissionFields &&
      mockCommissionManualFieldsRef.current
    ) {
      const manualCommissionSaved = await mockCommissionManualFieldsRef.current.save();
      if (!manualCommissionSaved) return;
    }

    if (form.additionalFields) {
      const existingAdditionalFields = documentFields.withData.find(f => f.name === 'additionalFields')?.value || {};
      form.additionalFields = { ...existingAdditionalFields, ...form.additionalFields };
    }

    if (form.customEntities) {
      const existingCustomEntities = documentFields.withData.find(f => f.name === 'customEntities')?.value || {};
      form.customEntities = { ...existingCustomEntities, ...form.customEntities };
    }

    const controller = new AbortController();
    const { signal } = controller;

    abortControllerRef.current = controller;

    let requestOptions = isMockPrint
      ? {
        endpoint: saveMode
          ? docSource === 'Contract'
            ? ENDPOINTS.CONTRACTS_BASE
            : ENDPOINTS.CONTACTS_BASE
          : ENDPOINTS.DOCHUB_PRINT_DOCUMENT
      }
      : {
        endpoint: saveMode
          ? docSource === 'Contract'
            ? ENDPOINTS.CONTRACTS_WITH_ID(selectedGroup._id)
            : ENDPOINTS.CONTACTS_WITH_ID(selectedGroup._id)
          : ENDPOINTS.DOCHUB_PRINT_DOCUMENT_WITH_ID(selectedGroup._id),
        method: saveMode ? 'PUT' : 'POST'
      };

    let printSessionId;
    if (saveMode) {
      if (isMockPrint) {
        //need to send inventoryunit because mock print does not save it to contact or contract on pick
        form['inventoryUnit'] = inventoryUnitMeta
          ? docSource === 'Contact'
            ? inventoryUnitMeta['inventoryUnits'].map(u => u._id)
            : inventoryUnitMeta['pickedInventory']
              ? inventoryUnitMeta['pickedInventory']['_id']
              : undefined
          : undefined;
      }
      requestOptions.requestBody = form;
    } else {
      form['inventoryUnit'] =
        inventoryUnitMeta && inventoryUnitMeta['pickedInventory']
          ? inventoryUnitMeta['pickedInventory']['_id']
          : undefined;
      const documentParams = isServiceRequest
        ? { serviceOrder: selectedGroup['serviceOrderReport'] || [], ...form }
        : form;

      printSessionId = uuid();
      requestOptions.requestBody = {
        documentParams,
        docId: documentId,
        documentName,
        dataSource: isMockPrint ? 'Mock' : docSource,
        printSessionId
      };
    }

    if (
      isMockPrint &&
      !inventoryUnitMeta?.pickedInventory &&
      customMockCommissionFields
    ) {
      requestOptions.requestBody['additionalMockParams'] = {
        inventoryUnit: customMockCommissionFields
      };
    }

    if (saveMode) {
      setSavingData(true);
    } else {
      setPrintingDocument({ printSessionId });
    }

    toast(`Please wait, ${saveMode ? 'saving data' : 'creating document'}...`, {
      type: 'info'
    });

    setCreatedDocument(null);
    const { error, response } = await makeApiRequests({ ...requestOptions, signal });

    if (error === ABORT_ERROR) return;

    if (error) {
      toast(error, {
        type: 'error'
      });
    } else {
      if (saveMode) {
        toast.success(`${docSource} saved successfully`);
        if (onUpdateDocHub) {
          onUpdateDocHub(response);
        }
      } else {
        setCreatedDocument({ originalName: response['metadata']['originalName'], ...response });
        if (onUpdateDocHub) {
          onUpdateDocHub(response?.updatedObject, true);
        }
      }
    }
    setPrintingDocument(false);
    setSavingData(false);
  };

  const autoFillAddress = (e, { stateKey, cityKey, countyKey }) => {
    try {
      const zipCode = e ? e.target.value : document.getElementById('mailingZip').value;
      const zipCodeFiltered = appChoices
        .find(a => a.key === 'zipCodes')
      ['values'].filter(zip => zipCode == zip['Zip Code']);

      if (zipCodeFiltered.length > 0) {
        document.getElementById(stateKey).value = zipCodeFiltered[0]['Property State'];
        document.getElementById(cityKey).value = zipCodeFiltered[0]['Property City'];
        document.getElementById(countyKey).value = zipCodeFiltered[0]['Property County'];
      }
    } catch (e) { }
  };

  window['autoFillMailingAddress'] = e =>
    autoFillAddress(e, { stateKey: 'mailingState', cityKey: 'mailingCity', countyKey: 'mailingCounty' });
  window['autoFillpropertyAddress'] = e =>
    autoFillAddress(e, { stateKey: 'propertyState', cityKey: 'propertyCity', countyKey: 'propertyCounty' });

  window['onPrintSubmit'] = onPrintSubmit;

  window['calculateSalesSummary'] = () => {
    try {
      calculateSalesSummary();
    } catch (e) {
      console.log('calculateSalesSummary', e);
    }
  };

  window['calculateCommissionsSummary'] = () => {
    try {
      calculateCommissionsSummary(getInventoryUnitForCommissionSummary());
    } catch (e) {
      console.log('calculateCommissionsSummary', e);
    }
  };

  return (
    <>
      {formJson && (isMockPrint || formPreFill) && (
        <>
          {isServiceRequest && (
            <>
              <Alert variant="primary" className="p-2">
                <div className="d-flex">
                  <div className="flex-grow-1 align-self-center">
                    <h6 className="mb-0">
                      <b>Service Order Report</b>
                    </h6>
                  </div>
                  <div>
                    <Button variant="primary" size="sm" className=" m-0 py-0" onClick={onServiceOrderReportClick}>
                      {selectedGroup['serviceOrderReport'] ? 'View' : 'Start'}
                    </Button>
                  </div>
                </div>
              </Alert>
              <ServiceReportModal
                show={showServiceReportModal}
                onHide={handleServiceReportClose}
                serviceRowSubmitting={serviceRowSubmitting}
                contract={selectedGroup}
                onServiceOrderRowsChange={onServiceOrderRowsChange}
                onSubmit={submitServiceReport}
              />
              <hr />
            </>
          )}

          {inventoryUnitMeta !== null && (
            <>
              <div className="mb-3">
                {['Contract', 'Inventory'].includes(docSource) ? (
                  <ContractedUnitOverview
                    onRefreshUnitClick={!isMockPrint && docSource === 'Contract' ? onRefreshUnitClick : undefined}
                    onLinkUnitClick={docSource === 'Contract' && onLinkUnitClick}
                    onEditUnitClick={
                      docSource === 'Contract' &&
                      (() =>
                        setInventoryUnitMeta({
                          ...inventoryUnitMeta,
                          editingInventory: inventoryUnitMeta['pickedInventory']
                        }))
                    }
                    onRemoveUnitClick={docSource === 'Contract' && onRemoveUnitClick}
                    onChangeUnitClick={docSource === 'Contract' && onLinkUnitClick}
                    inventoryUnit={inventoryUnitMeta['pickedInventory']}
                    refreshing={inventoryUnitMeta['refreshingInventory']}
                  />
                ) : (
                  <PotentialUnitsOverview
                    onRefreshUnitClick={!isMockPrint ? onRefreshUnitClick : undefined}
                    onLinkNewUnitClick={onLinkUnitClick}
                    onRemoveUnitClick={onRemoveUnitClick}
                    onChangeUnitClick={onLinkUnitClick}
                    inventoryUnits={inventoryUnitMeta['inventoryUnits']}
                    refreshing={inventoryUnitMeta['refreshingInventory']}
                    activeUnitId={
                      inventoryUnitMeta['pickedInventory'] ? inventoryUnitMeta['pickedInventory']['_id'] : ''
                    }
                    onEditUnitClick={unit =>
                      setInventoryUnitMeta({
                        ...inventoryUnitMeta,
                        editingInventory: unit
                      })
                    }
                    onUnitSelect={unit =>
                      setInventoryUnitMeta(prev => ({
                        ...prev,
                        pickedInventory: prev.inventoryUnits?.find(u => u._id === unit._id) || unit
                      }))
                    }
                    showCommissionFields={showInventoryCommissionFields}
                    customMockCommissionFields={isMockPrint && customMockCommissionFields}
                    onCustomMockCommissionFieldsChange={
                      isMockPrint ? setCustomMockCommissionFields : undefined
                    }
                    mockCommissionManualFieldsRef={isMockPrint ? mockCommissionManualFieldsRef : undefined}
                    appChoices={appChoices}
                  />
                )}
              </div>
              {showInventoryCommissionFields && selectedInventoryForCommission && (
                <Card className={`mb-3${commissionOptionsCollapsed ? ' overflow-hidden' : ''}`}>
                  <Card.Header
                    className="bg-primary-light d-flex align-items-center hover-light"
                    style={{
                      cursor: 'pointer',
                      ...(commissionOptionsCollapsed && {
                        borderBottomLeftRadius: 'calc(0.25rem - 1px)',
                        borderBottomRightRadius: 'calc(0.25rem - 1px)'
                      })
                    }}
                    onClick={() => setCommissionOptionsCollapsed(prev => !prev)}
                  >
                    <h6 className="mb-0 flex-grow-1">Commission Options</h6>
                    {commissionOptionsCollapsed ? <ArrowDownCircle size={16} /> : <ArrowUpCircle size={16} />}
                  </Card.Header>
                  <Card.Body className={`p-2${commissionOptionsCollapsed ? ' d-none' : ''}`}>
                    <InventoryCommissionFields
                      ref={commissionFieldsRef}
                      key={selectedInventoryForCommission._id}
                      inventory={selectedInventoryForCommission}
                      appChoices={appChoices}
                      onInventoryUpdate={onInventoryCommissionUpdate}
                      onValuesChange={onDraftInventoryFieldsChange}
                    />
                    <InventoryPnlFields
                      ref={pnlFieldsRef}
                      key={`${selectedInventoryForCommission._id}-pnl`}
                      inventory={selectedInventoryForCommission}
                      appChoices={appChoices}
                      onInventoryUpdate={onInventoryCommissionUpdate}
                      onValuesChange={onDraftInventoryFieldsChange}
                    />
                  </Card.Body>
                </Card>
              )}
              <InventoryPicker
                excludedUnitIds={
                  docSource === 'Contract'
                    ? inventoryUnitMeta['pickedInventory']
                      ? [inventoryUnitMeta['pickedInventory']['_id']]
                      : []
                    : docSource !== 'Inventory'
                      ? inventoryUnitMeta?.['inventoryUnits']
                        ? inventoryUnitMeta?.['inventoryUnits']?.map(u => u['_id'])
                        : []
                      : []
                }
                show={inventoryUnitMeta && inventoryUnitMeta['showInventoryPicker']}
                onSubmit={onInventoryPicked}
                submitting={inventoryUnitMeta && inventoryUnitMeta['assigningInventory']}
                fromContracts={docSource === 'Contracts'}
                onInventoryPickerClose={onInventoryPickerClose}
                selectedUnits={
                  inventoryUnitMeta && inventoryUnitMeta.selectedUnit ? [inventoryUnitMeta.selectedUnit] : []
                }
                onUnitSelect={onUnitSelect}
              />
              <InventoryEditSideBar
                appChoices={appChoices}
                onHide={() => setInventoryUnitMeta({ ...inventoryUnitMeta, editingInventory: null })}
                editingInventory={inventoryUnitMeta['editingInventory']}
                onInventoryEdit={onInventoryEdit}
                onInventoryDelete={onInventoryDelete}
                refetchChoices={refetchChoices}
              />

              <AlertModal
                show={inventoryUnitMeta['toBeDeletedUnit']}
                alertText={`Are you sure to remove this inventory unit from this ${docSource}?`}
                showProgress={inventoryUnitMeta['deletingUnit']}
                progressText="Removing unit..."
                onDismissClick={() => setInventoryUnitMeta({ ...inventoryUnitMeta, toBeDeletedUnit: undefined })}
                onHide={() => setInventoryUnitMeta({ ...inventoryUnitMeta, toBeDeletedUnit: undefined })}
                onContinueClick={removeInventoryUnit}
              />
            </>
          )}
          <Card className="mb-0">
            <Card.Header className="bg-primary-light">
              <h6 className="mb-0">{documentName}</h6>
            </Card.Header>
            <Card.Body className="p-2 dochub-document-scroll-pad">
              <FormGenerator formJson={formJson} formValues={{ document: formPreFill }} />
            </Card.Body>
          </Card>
          <div className="text-right dochub-sticky-actions">
            {docSource !== 'Inventory' && (
              <Button
                disabled={printingDocument || savingData}
                size="sm"
                variant="success"
                className="mr-2"
                onClick={onSaveClick}
              >
                Save
              </Button>
            )}
            <Button
              disabled={savingData}
              size="sm"
              variant={printingDocument ? 'danger' : 'primary'}
              onClick={onPrintClick}
            >
              {printingDocument ? 'Printing document... Tap again to cancel' : 'Print'}
            </Button>
          </div>
        </>
      )}
      {createdDocument && (
        <>
          <div ref={createdDocumentAlertRef}>
            <Alert variant="success" className="my-3 d-flex align-items-center">
              <p className="mb-0 flex-grow-1">
                The document "{createdDocument.originalName}" has been created successfully.{' '}
                {isMockPrint
                  ? ''
                  : `This has been added to the
              respective ${docSource} folder.`}{' '}
                You may quickly access the file here.
              </p>

              <Button className="mx-2" variant="success" onClick={() => setViewingFile(createdDocument)}>
                Open File
              </Button>
              <X size={25} className="ml-2 hover" onClick={() => setCreatedDocument(null)} />
            </Alert>
          </div>
          {viewingFile && <FilePopup file={viewingFile} onHide={() => setViewingFile(null)} />}
        </>
      )}
    </>
  );
};

export default DocumentPrinter;
